import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Box,
  Button,
  Divider,
  Paper,
  Typography,
  CircularProgress,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TitleWithAddButton from './TitleWithAddButton';
import {
  ROUTE_ADD_ENQUIRY,
  ROUTE_EVENTS,
  ROUTE_LANDING,
  SESSION_USER_ID_KEY,
  SESSION_USER_ROLES_PERMISSIONS,
} from '../../Util/VMConstants';
import { align_row, VM_Colors } from '../../Styles/VMStyles';
import MUITypography from '../../Components/MUITypography';
import StyledInputField from '../../Components/Custom/StyledInputField';
import StorageFactory from '../../Util/StorageFactory';
import { clearAuthToken } from '../../Util/jwtAuth';
import { toast } from 'react-toastify';
import { API_CHECK_AVAILABILITY } from '../../Util/URLConstants';
import { checkIsPermissionExist, isEmpty } from '../../Util/UtilHelper';

function VMHome() {
  const navigate = useNavigate();
  const [state, setState] = useState({
    selectedDate: dayjs(),
    search: '',
    subVenues: [],
    allSubVenues: [],
    loading: false,
  });

  const sessionFactory = new StorageFactory('session');
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);
  const VM_PERMISSIONS = sessionFactory.get(SESSION_USER_ROLES_PERMISSIONS);

  const lastSelectedDateRef = useRef(dayjs());

  const handleDateChange = (newDate) => {
    if (!newDate || lastSelectedDateRef.current.isSame(newDate, 'day')) {
      return;
    }
    lastSelectedDateRef.current = newDate;
    setState((prev) => ({
      ...prev,
      selectedDate: newDate,
    }));
  };

  useEffect(() => {
    if (!isEmpty(userID)) {
      fetchAvailability();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedDate]);

  // useEffect(() => {
  //   if (!isEmpty(userID)) {
  //     fetchAvailability(); // Initial load
  //   }
  // }, []); // Only on mount

  const fetchAvailability = async () => {
    try {
      setState((prev) => ({ ...prev, loading: true }));
      const formattedDate = state.selectedDate.format('DD-MM-YYYY');
      const response = await fetch(
        `${API_CHECK_AVAILABILITY}?user_id=${userID}&date=${formattedDate}`
      );
      const resp = await response.json();
      if (resp.status_code === 200) {
        setState((prev) => ({
          ...prev,
          subVenues: resp.data,
          allSubVenues: resp.data,
          loading: false,
        }));
      } else {
        if(resp.message === "Invalid User"){
          alert("Invalid User")
          clearAuthToken();
          sessionFactory.clear();
        //  navigate(`/${ROUTE_LOGIN}`)
            navigate(`/${ROUTE_LANDING}`)
        }
        toast.error(resp.message || 'Failed to load data');
        setState((prev) => ({ ...prev, loading: false }));
      }
    } catch (err) {
      toast.error(err.message || 'Something went wrong');
      setState((prev) => ({ ...prev, loading: false }));
    }
  };

  const mealOptions = ['Breakfast', 'Lunch', 'Dinner'];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  return (
    <Box
      sx={{
        p: 3,
        // ml: { md: 5 },
        fontFamily: 'Lato',
        backgroundColor: VM_Colors.white,
        borderRadius: 2,
      }}
    >
      <TitleWithAddButton
        title="Check Availability"
        onAddClick={() => navigate(`/${ROUTE_ADD_ENQUIRY}`)}
        buttonLabel="Add Enquiry"
        isButtonDisplay={checkIsPermissionExist(VM_PERMISSIONS,'VM001')}
           
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'flex-start' },
          gap: 3,
          mt: 2,
          width: '100%',
          minWidth: 0,
        }}
      >
        {/* Calendar */}
        <Box
          sx={{
            flex: '0 0 auto',
            width: { xs: '100%', md: 320 },
            maxWidth: { xs: 360, md: 320 },
            alignSelf: { xs: 'center', md: 'flex-start' },
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 2,
              borderRadius: 2,
              fontFamily: 'Lato',
              width: '100%',
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateCalendar
                value={state.selectedDate}
                onChange={handleDateChange}
                views={['year', 'month', 'day']}
                sx={{
                  overflowY: 'auto',
                  fontFamily: 'Lato',
                  '& .MuiTypography-root': {
                    fontFamily: 'Lato',
                  },
                  '& .MuiPickersCalendarHeader-label': {
                    fontWeight: 600,
                  },
                  '& .MuiDayCalendar-weekDayLabel': {
                    fontWeight: 500,
                  },
                }}
              />
            </LocalizationProvider>
          </Paper>
        </Box>

        {/* Sub Venues List */}
        <Box
          sx={{
            flex: '1 1 0',
            minWidth: 0,
            width: { xs: '100%', md: 'auto' },
          }}
        >
          {state.loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: 300,
                width: '100%',
              }}
            >
              <CircularProgress />
            </Box>
          ) : state.allSubVenues.length > 0 ? (
            <Paper
              elevation={3}
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: VM_Colors.white1,
                fontFamily: 'Lato',
                minWidth: 0,
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  mb: 2,
                }}
              >
                <MUITypography variant="h2">
                  {state.selectedDate.format('DD-MM-YYYY')}
                </MUITypography>
                {/* <MUITypography variant="h4">
                  Count: {state.subVenues.length}
                </MUITypography> */}
              </Box>

              <StyledInputField
                name="search"
                fullWidth
                placeholder="Search by hall name"
                inputProps={{ 'aria-label': 'Search by hall name' }}
                value={state.search}
                onChange={(e) => {
                  const searchText = e.target.value;
                  const filtered = state.allSubVenues.filter((venue) =>
                    venue.sub_venue_name
                      ?.toLowerCase()
                      .includes(searchText.toLowerCase()) ||
                    venue.sub_venue_id?.toString().includes(searchText)
                  );
                  setState((prev) => ({
                    ...prev,
                    search: searchText,
                    subVenues: filtered,
                  }));
                }}
              />

              <Box sx={{ mt: 2 }}>
                {state.subVenues.map((venue) => (
                  <Box
                    key={venue.sub_venue_id}
                    sx={{
                      mb: 2,
                      p: { xs: 1.5, sm: 2 },
                      borderRadius: 2,
                      boxShadow: 2,
                      bgcolor: '#fff',
                      fontFamily: 'Lato',
                      minWidth: 0,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        mb: 1,
                        gap: 1,
                      }}
                    >
                      <Box sx={align_row}>
                        <MUITypography fontWeight="bold" 
                        onClick={() =>{
                            if ((Number(venue.total_enquiry_count) > 0)) {
                              navigate({
                                pathname: "/" + ROUTE_EVENTS,
                                search: `?type=events&date=${state.selectedDate}`,
                              })
                            } 
                          }
                        }
                        >
                          {venue.sub_venue_name}   ({venue.total_enquiry_count}{Number(venue.total_enquiry_count)>1 ? "Enquiries" : "Enquiry"}) 
                        </MUITypography>
                        {Number(venue.total_enquiry_count) > 0 ? (
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            endIcon={<ChevronRightIcon />}
                            onClick={() => {
                              navigate({
                                pathname: '/' + ROUTE_EVENTS,
                                search: `?type=events&date=${state.selectedDate}`,
                              });
                            }}
                            sx={{
                              ml: 2,
                              textTransform: 'none',
                              fontWeight: 500,
                              borderRadius: 1.5,
                            }}
                          >
                            View
                          </Button>
                        ) : null}
                    
                      </Box>

                      <Typography variant="body2" color="text.secondary">
                        Capacity: {venue.sub_venue_capacity}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        {venue.sub_venue_type}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                        gap: { xs: 1, sm: 1.5 },
                        width: '100%',
                        minWidth: 0,
                      }}
                    >
                      {mealOptions.map((meal) => {
                        const isBooked = venue.event_booking_for.includes(meal);
                        return (
                          <Box
                            key={meal}
                            sx={{
                              minWidth: 0,
                              textAlign: 'center',
                              borderRadius: 1,
                              p: { xs: 0.75, sm: 1 },
                              border: '1px solid',
                              borderColor: isBooked
                                ? 'error.main'
                                : 'success.main',
                              backgroundColor: VM_Colors.white,
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                lineHeight: 1.2,
                                mb: 0.5,
                                display: 'block',
                              }}
                            >
                              {meal}
                            </Typography>
                            <Button
                              fullWidth
                              size="small"
                              variant="contained"
                              color={isBooked ? 'error' : 'success'}
                              sx={{
                                fontWeight: 600,
                                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                px: 0.5,
                                py: 0.5,
                                minHeight: { xs: 28, sm: 30 },
                                whiteSpace: 'normal',
                                lineHeight: 1.1,
                                backgroundColor: isBooked
                                  ? '#f44336'
                                  : '#2e7d32',
                                '&:hover': {
                                  backgroundColor: isBooked
                                    ? '#f44336'
                                    : '#2e7d32',
                                },
                                pointerEvents: isBooked ? 'none' : 'auto',
                              }}
                            >
                              {isBooked ? 'Not Available' : 'Available'}
                            </Button>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Paper>
          ) : (
            <Box
              sx={{
                p: 2,
                textAlign: 'center',
                color: 'text.secondary',
                minHeight: 200,
              }}
            >
              No sub-venues found. Please select a date.
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default VMHome;
